import React, { useMemo } from 'react';
import { Box, IconButton, Typography } from 'extended-oxygen-elements';
import { SiteDutyConfig } from '../types';
import { formatDutyRequirements } from '../utils/dataTransformers';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import * as TableStyles from '../styles/tableStyles';

type DutyRowProps = {
  config: SiteDutyConfig;
  onConfigure: (dutyType: string) => void;
  icon?: React.ComponentType<{ sx?: any }> | null;
};

export const DutyRow = React.memo(function DutyRow({ config, onConfigure, icon: Icon }: DutyRowProps) {
  const formattedValues = useMemo(() => {
    const requirements = formatDutyRequirements(config);
    return {
      requirements,
      timePeriods: requirements.map(r => r.timeRange).join('\n'),
      minPersonnel: requirements.map(r => r.minPersonnel).join('\n'),
      label: config.duty.name
    };
  }, [config]);

  return (
    <>
      {/* Desktop Layout - Table Row */}
      <Box
        sx={TableStyles.desktopRowStyles}
        role="row"
        aria-label={`${formattedValues.label} duty`}
      >
        {/* Icon + Duty Name */}
        <Box role="cell" sx={TableStyles.dutyCellStyles}>
          {Icon && <Icon sx={TableStyles.dutyIconStyles} aria-hidden="true" />}
          <Typography variant="body2" sx={TableStyles.dutyNameTypographyStyles}>
            {formattedValues.label}
          </Typography>
        </Box>

        {/* Times Required */}
        <Box role="cell" sx={TableStyles.timesCellStyles}>
          <Typography variant="body2" sx={TableStyles.timesTypographyStyles}>
            {formattedValues.timePeriods}
          </Typography>
        </Box>

        {/* No. People */}
        <Box role="cell" sx={TableStyles.peopleCellStyles}>
          <Typography variant="body2" sx={TableStyles.peopleTypographyStyles}>
            {formattedValues.minPersonnel}
          </Typography>
        </Box>

        {/* Configure Button */}
        <Box role="cell" sx={TableStyles.configureCellStyles}>
          <IconButton
            onClick={() => onConfigure(config.duty.id)}
            aria-label={`Configure ${formattedValues.label}`}
            sx={TableStyles.configureIconButtonStyles}
          >
            <SettingsOutlinedIcon sx={TableStyles.configureIconStyles} />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile Layout - Card Style */}
      <Box
        sx={TableStyles.mobileRowStyles}
        role="row"
        aria-label={`${formattedValues.label} duty`}
      >
        {/* Row 1: Icon + Name */}
        <Box sx={TableStyles.mobileRowHeaderStyles}>
          {Icon && <Icon sx={TableStyles.mobileDutyIconStyles} aria-hidden="true" />}
          <Typography variant="body2" sx={TableStyles.mobileDutyNameTypographyStyles}>
            {formattedValues.label}
          </Typography>
        </Box>

        {/* Row 2: Times and People info */}
        <Box sx={TableStyles.mobileInfoSectionStyles}>
          <Box sx={TableStyles.mobileTimesSectionStyles}>
            <Typography variant="caption" sx={TableStyles.mobileSectionLabelStyles}>
              Times Required
            </Typography>
            <Typography variant="body2" sx={TableStyles.mobileSectionValueStyles}>
              {formattedValues.timePeriods}
            </Typography>
          </Box>
          <Box sx={TableStyles.mobilePeopleSectionStyles}>
            <Typography variant="caption" sx={TableStyles.mobileSectionLabelStyles}>
              No. People
            </Typography>
            <Typography variant="body2" sx={TableStyles.mobileSectionValueStyles}>
              {formattedValues.minPersonnel}
            </Typography>
          </Box>
        </Box>

        {/* Row 3: Configure Button */}
        <Box sx={TableStyles.mobileConfigureContainerStyles}>
          <IconButton
            onClick={() => onConfigure(config.duty.id)}
            aria-label={`Configure ${formattedValues.label}`}
            sx={TableStyles.mobileConfigureIconButtonStyles}
          >
            <SettingsOutlinedIcon sx={TableStyles.mobileConfigureIconStyles} />
          </IconButton>
        </Box>
      </Box>
    </>
  );
});
